<template>
    <div class="relative w-full">
        <UPopover :popper="{ placement: 'bottom-start' }" class="w-full">
            <UInput
                :model-value="displayValue"
                :placeholder="placeholder || 'مثال: 1375/05/12'"
                :size="size"
                :disabled="disabled"
                readonly
                trailing-icon="i-material-symbols-light:calendar-today-outline"
                class="w-full cursor-pointer"
                :ui="{ icon: { trailing: { pointer: '' } } }"
            />

            <template #panel="{ close }">
                <div class="p-3 w-72 bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-100 dark:border-gray-800 text-sm select-none" dir="rtl">
                    <!-- Header: Month/Year navigation & dropdowns -->
                    <div class="flex items-center justify-between gap-1 mb-3">
                        <button
                            type="button"
                            class="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300 transition"
                            @click="prevMonth"
                            aria-label="ماه قبل"
                        >
                            <UIcon name="i-material-symbols-light:chevron-right" class="w-5 h-5" />
                        </button>

                        <div class="flex items-center gap-1.5 font-medium text-gray-800 dark:text-gray-200">
                            <!-- Month Select -->
                            <select
                                v-model="currentMonth"
                                class="bg-transparent border border-gray-200 dark:border-gray-700 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500 cursor-pointer"
                            >
                                <option
                                    v-for="(name, index) in persianMonths"
                                    :key="index"
                                    :value="index"
                                    class="text-gray-800 dark:text-gray-200 dark:bg-gray-900"
                                >
                                    {{ name }}
                                </option>
                            </select>

                            <!-- Year Select -->
                            <select
                                v-model="currentYear"
                                class="bg-transparent border border-gray-200 dark:border-gray-700 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500 cursor-pointer"
                            >
                                <option
                                    v-for="year in availableYears"
                                    :key="year"
                                    :value="year"
                                    class="text-gray-800 dark:text-gray-200 dark:bg-gray-900"
                                >
                                    {{ year }}
                                </option>
                            </select>
                        </div>

                        <button
                            type="button"
                            class="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300 transition"
                            @click="nextMonth"
                            aria-label="ماه بعد"
                        >
                            <UIcon name="i-material-symbols-light:chevron-left" class="w-5 h-5" />
                        </button>
                    </div>

                    <!-- Weekdays Header -->
                    <div class="grid grid-cols-7 gap-1 text-center text-xs font-semibold text-gray-400 dark:text-gray-500 mb-2">
                        <span v-for="day in weekDays" :key="day">{{ day }}</span>
                    </div>

                    <!-- Days Grid -->
                    <div class="grid grid-cols-7 gap-1 text-center">
                        <span
                            v-for="empty in leadingOffset"
                            :key="`empty-${empty}`"
                            class="h-7 w-7"
                        />

                        <button
                            v-for="day in daysInMonth"
                            :key="day"
                            type="button"
                            :class="[
                                'h-7 w-7 rounded-lg text-xs font-medium flex items-center justify-center transition',
                                isSelected(day)
                                    ? 'bg-primary-600 text-white shadow-sm'
                                    : isToday(day)
                                      ? 'border border-primary-500 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-950/30'
                                      : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
                            ]"
                            @click="selectDay(day, close)"
                        >
                            {{ day }}
                        </button>
                    </div>

                    <!-- Footer Action Buttons -->
                    <div class="mt-3 pt-2.5 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between text-xs">
                        <button
                            type="button"
                            class="text-primary-600 dark:text-primary-400 hover:underline font-medium"
                            @click="selectToday(close)"
                        >
                            امروز
                        </button>
                        <button
                            v-if="modelValue"
                            type="button"
                            class="text-gray-400 hover:text-rose-500 transition"
                            @click="clearValue(close)"
                        >
                            پاک کردن
                        </button>
                    </div>
                </div>
            </template>
        </UPopover>
    </div>
</template>

<script setup lang="ts">
import moment from "jalali-moment";

const props = withDefaults(
    defineProps<{
        modelValue?: string | null;
        placeholder?: string;
        disabled?: boolean;
        size?: "2xs" | "xs" | "sm" | "md" | "lg" | "xl";
        minYear?: number;
        maxYear?: number;
    }>(),
    {
        modelValue: "",
        placeholder: "",
        disabled: false,
        size: "sm",
        minYear: 1300,
        maxYear: 1430,
    }
);

const emit = defineEmits<{
    (e: "update:modelValue", value: string): void;
    (e: "change", value: string): void;
}>();

const persianMonths = [
    "فروردین",
    "اردیبهشت",
    "خرداد",
    "تیر",
    "مرداد",
    "شهریور",
    "مهر",
    "آبان",
    "آذر",
    "دی",
    "بهمن",
    "اسفند",
];

const weekDays = ["ش", "ی", "د", "س", "چ", "پ", "ج"];

// Initialize calendar state with modelValue or current date
const now = moment();
const initialMoment = props.modelValue && moment(props.modelValue, "jYYYY/jMM/jDD").isValid()
    ? moment(props.modelValue, "jYYYY/jMM/jDD")
    : now;

const currentYear = ref(initialMoment.jYear());
const currentMonth = ref(initialMoment.jMonth()); // 0-indexed

watch(
    () => props.modelValue,
    (newVal) => {
        if (newVal && moment(newVal, "jYYYY/jMM/jDD").isValid()) {
            const m = moment(newVal, "jYYYY/jMM/jDD");
            currentYear.value = m.jYear();
            currentMonth.value = m.jMonth();
        }
    }
);

const availableYears = computed(() => {
    const list: number[] = [];
    for (let y = props.maxYear; y >= props.minYear; y--) {
        list.push(y);
    }
    return list;
});

const daysInMonth = computed(() => {
    return moment.jDaysInMonth(currentYear.value, currentMonth.value);
});

// Calculate leading empty cells so day 1 matches its day of week
// Jalali week starts on Saturday (Shanbeh)
const leadingOffset = computed(() => {
    const firstDay = moment(`${currentYear.value}/${currentMonth.value + 1}/1`, "jYYYY/jMM/jDD");
    // day(): 0=Sun, 1=Mon, ..., 6=Sat
    // Iranian: Sat=0, Sun=1, Mon=2, Tue=3, Wed=4, Thu=5, Fri=6
    return (firstDay.day() + 1) % 7;
});

const displayValue = computed(() => {
    if (!props.modelValue) return "";
    return props.modelValue;
});

function isSelected(day: number): boolean {
    if (!props.modelValue) return false;
    const formatted = formatJalali(currentYear.value, currentMonth.value, day);
    return props.modelValue === formatted;
}

function isToday(day: number): boolean {
    const today = moment();
    return (
        today.jYear() === currentYear.value &&
        today.jMonth() === currentMonth.value &&
        today.jDate() === day
    );
}

function formatJalali(year: number, monthIndex: number, day: number): string {
    const m = String(monthIndex + 1).padStart(2, "0");
    const d = String(day).padStart(2, "0");
    return `${year}/${m}/${d}`;
}

function selectDay(day: number, close?: () => void) {
    const formatted = formatJalali(currentYear.value, currentMonth.value, day);
    emit("update:modelValue", formatted);
    emit("change", formatted);
    close?.();
}

function selectToday(close?: () => void) {
    const today = moment();
    currentYear.value = today.jYear();
    currentMonth.value = today.jMonth();
    selectDay(today.jDate(), close);
}

function clearValue(close?: () => void) {
    emit("update:modelValue", "");
    emit("change", "");
    close?.();
}

function prevMonth() {
    if (currentMonth.value === 0) {
        currentMonth.value = 11;
        currentYear.value--;
    } else {
        currentMonth.value--;
    }
}

function nextMonth() {
    if (currentMonth.value === 11) {
        currentMonth.value = 0;
        currentYear.value++;
    } else {
        currentMonth.value++;
    }
}
</script>
